import { Button, Input } from '@artosera/ui';
import { useState, type FormEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { naviguerVers, urlDebutSso } from '../../api/client.js';
import { modeDemonstrationActif } from '../../api/demo.js';
import { capturerPortee, exigerActuelle } from '../../auth/identite.js';
import { retourSur } from '../../auth/retour.js';
import { useApresConnexion } from '../../auth/session.js';
import { AuthCard } from '../AuthCard.js';
import { Field } from '../Field.js';
import styles from './ConnexionPage.module.css';

const ERREURS = new Set([
  'indisponible',
  'refuse_fournisseur',
  'etat_invalide',
  'echange_refuse',
  'compte_non_lie',
  'identite_deja_liee',
  'compte_console',
]);

export function ConnexionSsoPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const [parametres] = useSearchParams();
  const [slug, setSlug] = useState((parametres.get('galerie') ?? '').toLowerCase().slice(0, 100));
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [simulation, setSimulation] = useState(false);
  const oublierPrecedente = useApresConnexion();
  const code = parametres.get('erreur');
  const message =
    erreur ?? (code ? t(`auth.sso.erreurs.${ERREURS.has(code) ? code : 'inconnue'}`) : null);

  async function soumettre(event: FormEvent) {
    event.preventDefault();
    const galerie = slug.trim().toLowerCase();
    if (!/^[a-z0-9-]{1,100}$/.test(galerie)) {
      setErreur(t('auth.sso.galerieInvalide'));
      return;
    }
    setErreur(null);
    if (modeDemonstrationActif()) {
      setSimulation(true);
      return;
    }
    setEnCours(true);
    try {
      // Le retour est une navigation complète : invalider aussi l'instantané
      // hors ligne précédent avant que le cookie puisse changer au rappel.
      const oubli = oublierPrecedente();
      const portee = capturerPortee();
      await oubli;
      exigerActuelle(portee);
      naviguerVers(urlDebutSso(galerie, retourSur(parametres.get('retour') ?? `/${galerie}`)));
    } catch {
      setErreur(t('auth.sso.erreurs.inconnue'));
      setEnCours(false);
    }
  }

  return (
    <AuthCard title={t('auth.sso.titre')} subtitle={t('auth.sso.sousTitre')}>
      <form onSubmit={(event) => void soumettre(event)} noValidate>
        <Field label={t('auth.sso.champGalerie')} htmlFor="sso-galerie">
          <Input
            id="sso-galerie"
            value={slug}
            onChange={(event) => setSlug(event.target.value.toLowerCase())}
            required
            maxLength={100}
            pattern="[a-z0-9-]+"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="organization"
            aria-describedby="sso-aide"
            className={styles.pleineLargeur}
          />
          <p id="sso-aide" className={styles.aideSso}>
            {t('auth.sso.aideGalerie')}
          </p>
        </Field>
        {simulation && <p role="status">{t('auth.sso.simulation')}</p>}
        {message && (
          <p role="alert" className={styles.erreur}>
            {message}
          </p>
        )}
        <Button type="submit" className={`${styles.envoi} ${styles.envoiSso}`} loading={enCours}>
          {t('auth.sso.continuer')}
        </Button>
      </form>
      <p className={styles.lienOubli}>
        <Link to={`/connexion?retour=${encodeURIComponent(retourSur(parametres.get('retour')))}`}>
          {t('auth.sso.motDePasse')}
        </Link>
      </p>
    </AuthCard>
  );
}
